import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CommunityMember } from '../../types/community';
import { Star, StarIcon, MapPinIcon, UsersIcon, CalendarIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toggleSavedMember, isMemberSaved } from '../../services/communityService';
interface DirectoryCardProps {
  member: CommunityMember;
  matches?: {
    id: string;
    name: string;
    date: string;
  }[];
}
export function DirectoryCard({
  member,
  matches = []
}: DirectoryCardProps) {
  const {
    currentUser
  } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState<boolean>(currentUser ? isMemberSaved(currentUser.id, member.id) : false);
  const [showMatches, setShowMatches] = useState(false);
  const handleSaveToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentUser) {
      navigate('/login', {
        state: {
          returnUrl: `/community/${member.id}`
        }
      });
      return;
    }
    toggleSavedMember(currentUser.id, member.id);
    setSaved(!saved);
  };
  const handleCardClick = () => {
    navigate(`/community/${member.id}`);
  };
  return <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md hover:border-indigo-100 transition-all group cursor-pointer relative" onClick={handleCardClick} data-id="element-415">
      {/* Save button */}
      <button className="absolute top-2 right-2 z-10 p-2 rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 transition-all" onClick={handleSaveToggle} title={saved ? 'Remove from saved' : 'Save for later'} data-id="element-416">
        {saved ? <StarIcon className="h-5 w-5 text-yellow-500 fill-current" data-id="element-417" /> : <Star className="h-5 w-5 text-gray-400 hover:text-yellow-500" data-id="element-418" />}
      </button>
      <div className="p-6" data-id="element-419">
        <div className="flex items-start" data-id="element-420">
          <div className="relative h-16 w-16 mr-4 flex-shrink-0" data-id="element-421">
            {member.logoUrl ? <img src={member.logoUrl} alt={member.name} className="h-full w-full object-contain rounded-md group-hover:shadow-[0_0_15px_rgba(99,102,241,0.4)] transition-all" data-id="element-422" /> : <div className={`h-full w-full rounded-md flex items-center justify-center text-white font-bold text-2xl ${member.type === 'brand' ? 'bg-blue-500' : 'bg-purple-500'}`} data-id="element-423">
                {member.name.charAt(0)}
              </div>}
            {member.featured && <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-800 rounded-full p-0.5" title="Featured" data-id="element-424">
                <Star className="h-3 w-3 fill-current" data-id="element-425" />
              </div>}
          </div>
          <div className="flex-grow" data-id="element-426">
            <div className="flex items-center justify-between" data-id="element-427">
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-700 transition-colors" data-id="element-428">
                {member.name}
              </h3>
              <span className={`text-xs px-2 py-1 rounded-full ${member.type === 'brand' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-429">
                {member.type === 'brand' ? 'Brand' : 'Organizer'}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600 line-clamp-2" data-id="element-430">
              {member.shortDescription}
            </p>
          </div>
        </div>
      </div>
      {/* Match preview section */}
      <div className={`px-6 py-3 text-xs border-t ${member.type === 'brand' ? 'border-blue-50 bg-blue-50' : 'border-purple-50 bg-purple-50'}`} data-id="element-431">
        <div className="flex justify-between items-center cursor-pointer" onClick={e => {
        e.stopPropagation();
        setShowMatches(!showMatches);
      }} data-id="element-432">
          <div className="flex items-center text-gray-700" data-id="element-433">
            <SparklesIcon className="h-3.5 w-3.5 mr-1.5" data-id="element-434" />
            <span data-id="element-435">
              {matches.length > 0 ? `${matches.length} matched ${member.type === 'brand' ? 'events' : 'brands'}` : `No matches yet`}
            </span>
          </div>
          <button className="text-indigo-600 hover:text-indigo-800 text-xs" data-id="element-436">
            {showMatches ? 'Hide' : 'Show'}
          </button>
        </div>
        {/* Expanded matches list */}
        {showMatches && matches.length > 0 && <div className="mt-2 space-y-2 pt-2 border-t border-gray-200 border-opacity-50" onClick={e => e.stopPropagation()} data-id="element-437">
            {matches.slice(0, 3).map(match => <div key={match.id} className="flex items-start" data-id="element-438">
                {member.type === 'brand' ? <CalendarIcon className="h-3.5 w-3.5 text-purple-500 mr-1.5 mt-0.5" data-id="element-439" /> : <div className="h-3.5 w-3.5 bg-blue-500 rounded-sm mr-1.5 mt-0.5" data-id="element-440" />}
                <div data-id="element-441">
                  <div className="text-xs font-medium text-gray-900" data-id="element-442">
                    {match.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-443">{match.date}</div>
                </div>
              </div>)}
            {matches.length > 3 && <div className="text-xs text-indigo-600 hover:text-indigo-800 mt-1" data-id="element-444">
                + {matches.length - 3} more matches
              </div>}
          </div>}
      </div>
    </div>;
}
function SparklesIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-445">
      <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" data-id="element-446" />
      <path d="M5 3v4" data-id="element-447" />
      <path d="M19 17v4" data-id="element-448" />
      <path d="M3 5h4" data-id="element-449" />
      <path d="M17 19h4" data-id="element-450" />
    </svg>;
}